// src/utils/mappers/repairMappers.ts
import { Repair, DbRepair, RepairCreate, RepairUpdate, DbRepairInsert, DbRepairUpdate } from '@/types/entities/repair';

/**
 * Маппер из DbRepair в Repair (snake_case -> camelCase)
 */
export const dbRepairToRepair = (dbRepair: DbRepair): Repair => ({
  id: dbRepair.id,
  userId: dbRepair.user_id,
  rentalPointId: dbRepair.rental_point_id,
  clientId: dbRepair.client_id,
  clientName: dbRepair.client_name,
  itemType: dbRepair.item_type,
  itemName: dbRepair.item_name,
  itemBrand: dbRepair.item_brand,
  itemModel: dbRepair.item_model,
  serialNumber: dbRepair.serial_number,
  issueDescription: dbRepair.issue_description,
  estimatedCompletionDate: dbRepair.estimated_completion_date ? new Date(dbRepair.estimated_completion_date) : undefined,
  urgentRepair: dbRepair.urgent_repair,
  technicianNotes: dbRepair.technician_notes,
  status: dbRepair.status,
  completedAt: dbRepair.completed_at ? new Date(dbRepair.completed_at) : undefined,
  laborCost: dbRepair.labor_cost,
  partsCost: dbRepair.parts_cost,
  totalCost: dbRepair.total_cost,
  createdAt: new Date(dbRepair.created_at),
  updatedAt: new Date(dbRepair.updated_at),
});

/**
 * Маппер из RepairCreate в DbRepairInsert (camelCase -> snake_case)
 */
export const repairCreateToDbRepairInsert = (repairCreate: RepairCreate, userId: string): DbRepairInsert => ({
  user_id: userId,
  rental_point_id: repairCreate.rentalPointId,
  client_id: repairCreate.clientId,
  item_type: repairCreate.itemType,
  item_name: repairCreate.itemName,
  item_brand: repairCreate.itemBrand,
  item_model: repairCreate.itemModel,
  serial_number: repairCreate.serialNumber,
  issue_description: repairCreate.issueDescription,
  estimated_completion_date: repairCreate.estimatedCompletionDate?.toISOString(),
  urgent_repair: repairCreate.urgentRepair,
  technician_notes: repairCreate.technicianNotes,
  status: repairCreate.status || 'pending',
  labor_cost: repairCreate.laborCost,
  parts_cost: repairCreate.partsCost,
  total_cost: repairCreate.totalCost,
});

/**
 * Маппер из RepairUpdate в DbRepairUpdate (camelCase -> snake_case)
 */
export const repairUpdateToDbRepairUpdate = (repairUpdate: RepairUpdate): DbRepairUpdate => {
  const dbUpdate: DbRepairUpdate = {};
  
  if (repairUpdate.rentalPointId !== undefined) dbUpdate.rental_point_id = repairUpdate.rentalPointId;
  if (repairUpdate.clientId !== undefined) dbUpdate.client_id = repairUpdate.clientId;
  if (repairUpdate.itemType !== undefined) dbUpdate.item_type = repairUpdate.itemType;
  if (repairUpdate.itemName !== undefined) dbUpdate.item_name = repairUpdate.itemName;
  if (repairUpdate.itemBrand !== undefined) dbUpdate.item_brand = repairUpdate.itemBrand;
  if (repairUpdate.itemModel !== undefined) dbUpdate.item_model = repairUpdate.itemModel;
  if (repairUpdate.serialNumber !== undefined) dbUpdate.serial_number = repairUpdate.serialNumber;
  if (repairUpdate.issueDescription !== undefined) dbUpdate.issue_description = repairUpdate.issueDescription;
  if (repairUpdate.estimatedCompletionDate !== undefined) {
    dbUpdate.estimated_completion_date = repairUpdate.estimatedCompletionDate?.toISOString();
  }
  if (repairUpdate.urgentRepair !== undefined) dbUpdate.urgent_repair = repairUpdate.urgentRepair;
  if (repairUpdate.technicianNotes !== undefined) dbUpdate.technician_notes = repairUpdate.technicianNotes;
  if (repairUpdate.status !== undefined) dbUpdate.status = repairUpdate.status;
  if (repairUpdate.completedAt !== undefined) {
    dbUpdate.completed_at = repairUpdate.completedAt?.toISOString();
  }
  if (repairUpdate.laborCost !== undefined) dbUpdate.labor_cost = repairUpdate.laborCost;
  if (repairUpdate.partsCost !== undefined) dbUpdate.parts_cost = repairUpdate.partsCost;
  if (repairUpdate.totalCost !== undefined) dbUpdate.total_cost = repairUpdate.totalCost;
  
  return dbUpdate;
};

/**
 * Утилита для подготовки данных ремонта для базы данных
 */
export const prepareRepairForDb = (repair: Partial<Repair>): Partial<DbRepair> => {
  const dbRepair: Partial<DbRepair> = {};
  
  if (repair.rentalPointId !== undefined) dbRepair.rental_point_id = repair.rentalPointId;
  if (repair.clientId !== undefined) dbRepair.client_id = repair.clientId;
  if (repair.itemType !== undefined) dbRepair.item_type = repair.itemType;
  if (repair.itemName !== undefined) dbRepair.item_name = repair.itemName;
  if (repair.itemBrand !== undefined) dbRepair.item_brand = repair.itemBrand;
  if (repair.itemModel !== undefined) dbRepair.item_model = repair.itemModel;
  if (repair.serialNumber !== undefined) dbRepair.serial_number = repair.serialNumber;
  if (repair.issueDescription !== undefined) dbRepair.issue_description = repair.issueDescription;
  if (repair.estimatedCompletionDate !== undefined) {
    dbRepair.estimated_completion_date = repair.estimatedCompletionDate?.toISOString();
  }
  if (repair.urgentRepair !== undefined) dbRepair.urgent_repair = repair.urgentRepair;
  if (repair.technicianNotes !== undefined) dbRepair.technician_notes = repair.technicianNotes;
  if (repair.status !== undefined) dbRepair.status = repair.status;
  if (repair.completedAt !== undefined) dbRepair.completed_at = repair.completedAt?.toISOString();
  if (repair.laborCost !== undefined) dbRepair.labor_cost = repair.laborCost;
  if (repair.partsCost !== undefined) dbRepair.parts_cost = repair.partsCost;
  if (repair.totalCost !== undefined) dbRepair.total_cost = repair.totalCost;
  if (repair.userId !== undefined) dbRepair.user_id = repair.userId;
  
  return dbRepair;
};
